import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../../common/withRouter';
import {
  DataDefinitionList,
  Loader,
  Tabs,
  IconWithText,
  Notification,
  Message
} from '../../../web.common';
import Participants from './Participants';
import Fields from './Fields';
import {
  DisplayDate,
  DisplayGuid,
  DisplayStatus,
  DisplayPrice,
  DisplayUser,
  DisplayPaymentMethod
} from '../../items';
import { purchaseStatus } from '../../../enums/purchaseStatus';
import { LayoutContext } from '../../layout/siteLayout';
import PageTop from '../../layout/PageTop';
import withApi from '../../higherOrderComponents/withApi';
import { DetailsRightBar, NoLink, Link, Divider } from '../DetailsRightBar';

class PurchaseInternal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      purchaseData: null,
      selectedProductIndex: 0,
      participantsData: {}
    };
  }

  async componentDidMount() {
    const { match: { params: { purchaseId } } } = this.props;
    const { api: { purchases: { getPurchase } } } = this.props;

    try {
      const purchaseData = await getPurchase(purchaseId);
      this.setState({ purchaseData });
    }
    catch (e) {
      this.setState({ errorMessage: new Message('error', 'Unable to get data') });
    }
  }

  renderProductDetails(product, index, total) {
    const label = total > 1 ? ` (${index + 1}/${total})` : '';

    return {
      [`Product Id${label}`]: <DisplayGuid baseAnchor='/product'>{product.ProductId}</DisplayGuid>,
      [`Purchase Prod. Id${label}`]: <DisplayGuid>{product.Id}</DisplayGuid>,
      [`Product Name${label}`]: product.ProductName,
      [`Quantity${label}`]: product.Quantity,
      [`Status${label}`]: <DisplayStatus mapping={purchaseStatus}>{product.Status}</DisplayStatus>,
      [`Purchase Text${label}`]: product.Text,
      [`Base Price${label}`]: `${product.BasePrice.toFixed(2).toString()} DKK`,
      [`Total Price${label}`]: `${product.GrandTotal.toFixed(2).toString()} DKK`,
      [`Billing At (CET)${label}`]: <DisplayDate>{product.BillingAtCet}</DisplayDate>,
      [`Latest Cancellation At (CET)${label}`]: <DisplayDate>{product.LatestCancellationAtCet}</DisplayDate>,
      [`Occurs At (CET)${label}`]: <DisplayDate>{product.OccursAtCet}</DisplayDate>
    };
  }

  render() {
    const { match: { params: { purchaseId } } } = this.props;
    const { purchaseData, selectedProductIndex, errorMessage } = this.state;

    let mappedData = null;
    const products = purchaseData ? purchaseData.Products : [];
    const hasMultipleProducts = products.length > 1;

    if (purchaseData) {
      const {
        Id,
        PaymentMethod,
        CreatedAtCet,
        UserId,
        UserName,
        UserEmail
      } = purchaseData;

      const purchaseInfo = {
        Id: <DisplayGuid>{Id}</DisplayGuid>,
        'User Id': <DisplayGuid baseAnchor='/user'>{UserId}</DisplayGuid>,
        User: <DisplayUser>{{ Name: UserName, Email: UserEmail }}</DisplayUser>,
        PaymentMethod: <DisplayGuid baseAnchor='paymentmethod/creditcard'>{PaymentMethod}</DisplayGuid>,
        'Created At (CET)': <DisplayDate>{CreatedAtCet}</DisplayDate>
      };

      if (hasMultipleProducts) {
        purchaseInfo['Products Count'] = products.length;
      }

      let productDetails = {};
      products.forEach((product, index) => {
        productDetails = {
          ...productDetails,
          ...this.renderProductDetails(product, index, products.length)
        };
      });

      mappedData = {
        ...purchaseInfo,
        ...productDetails
      };
    }

    const productTabs = products.map((product, index) => {
      const label = hasMultipleProducts ? ` #${index + 1} - ${product.ProductName}` : '';
      return [
        {
          tab: {
            content: (<IconWithText iconName='users' text={`Participants${label}`} />)
          },
          panel: {
            content: <Participants purchaseProductId={product.Id} />
          }
        },
        {
          tab: {
            content: (<IconWithText iconName='address-card' text={`Fields${label}`} />)
          },
          panel: {
            content: <Fields purchaseProductId={product.Id} />
          }
        }
      ];
    }).flat();

    return (
      <LayoutContext.Consumer>
        {() => (
          <React.Fragment>
            <Notification newMessage={errorMessage} />

            <div className='content-with-right-pane-delimiter'>
              <PageTop
                documentTitle='Purchase details'
                documentDescription='Purchase details'
              />

              <h2 className='main-page-header main-page-header-with-subheader'>
                Purchase
              </h2>
              <h5 className='main-page-subheader'>{purchaseId}</h5>
              <br />

              {
                purchaseData ? (
                  <React.Fragment>
                    <Tabs tablistAriaTitle='booking-details'>
                      {
                        [
                          {
                            tab: {
                              content: (<IconWithText iconName='info' text='Basic Info' />)
                            },
                            panel: {
                              content: <DataDefinitionList
                                listData={mappedData}
                                classNames={['detail-page-list']}
                              />
                            }
                          },
                          ...productTabs
                        ]
                      }
                    </Tabs>
                  </React.Fragment>
                ) : <Loader hasLoaded={false}>Loading...</Loader>
              }
            </div>

            <DetailsRightBar>
              {
                mappedData ? (
                  <React.Fragment>
                    <Divider>Links</Divider>
                    <Link to={`/transactions?purchaseId=${purchaseData.Id}`}>Transactions</Link>
                    <Link to={`/user/${purchaseData.UserId}`}>User</Link>
                    {
                      purchaseData.PaymentMethod ? <Link to={`/paymentmethod/${purchaseData.PaymentMethod.Type}/${purchaseData.PaymentMethod.Id}`}>
                        Payment Method
                      </Link> : ''
                    }
                    <Link to={`/signups?purchaseId=${purchaseData.Id}`}>Signups</Link>
                    <Divider>Actions</Divider>
                    <NoLink>None</NoLink>
                  </React.Fragment>
                ) : ''
              }
            </DetailsRightBar>


          </React.Fragment>
        )}
      </LayoutContext.Consumer>
    );
  }
}

const Purchase = withRouter(withApi(connect(({ user }) => ({ isLoggedIn: !!user }), {})(PurchaseInternal)));

Purchase.requiresLogin = true;
Purchase.propTypes = { isLoggedIn: PropTypes.bool };

export default Purchase;
